import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRightLong,
  faBoxOpen,
  faCheck,
  faChevronRight,
  faCircleDot,
  faLocationDot,
  faRefresh,
} from '@fortawesome/free-solid-svg-icons';
import { driverApi, driverKeys } from '../features/driver/driverApi';
import { scheduleLabel } from '../features/driver/schedule';
import {
  ORDER_STATUS_LABELS,
  ORDER_STATUS_TONES,
  STOP_TYPE_LABELS,
} from '../features/orders/labels';
import { useAuth } from '../features/auth/useAuth';
import { isApiError } from '../lib/apiError';
import { ROUTES, routeTo } from '../routes/paths';
import { Badge } from '../components/ui/Badge';
import { ProgressBar } from '../components/ui/ProgressBar';
import { EmptyState, ErrorState, LoadingState } from '../components/ui/PageState';
import type { DriverOrder } from '../types/api';
import { Button } from '../components/ui/Button';
import { Spinner } from '../components/ui/Spinner';

/**
 * Inicio del chofer: lo que le queda por hacer.
 *
 * Cada orden se lee sin abrirla —cuando es, entre que ubicaciones va, por que
 * parada va— porque la pregunta de esta pantalla es cual toca ahora, y abrir
 * tres para averiguarlo es la pregunta sin responder.
 *
 * Por omision no se listan las cerradas ni las canceladas: una lista que crece
 * cada dia con trabajo terminado deja de responder nada.
 */
export function DriverHomePage(): ReactNode {
  const { user } = useAuth();
  const [includeClosed, setIncludeClosed] = useState(false);
  const [loader, setLoader] = useState(false);

  const query = useQuery({
    queryKey: [...driverKeys.orders, { includeClosed }],
    queryFn: () => driverApi.listMine({ limit: 50, include_closed: includeClosed }),
  });

  const orders = query.data?.data ?? [];
  const firstName = user?.full_name.split(' ')[0] ?? '';
  const handleRefresh = async () => {
    setLoader(true);

    await query.refetch();

    setTimeout(() => {
      setLoader(false);
    }, 3000);
  };

  return (
    <div className="space-y-4 mt-2 relative">
      {loader && (
        <div
          className={`w-screen absolute -top-7 text-center flex justify-center items-center -left-4 h-6 ${query.isFetching ? 'bg-accent' : query.isError ? 'bg-red-400' : 'bg-green-400'}`}
        >
          <p className="text-center text-xs font-semibold">
            {query.isFetching
              ? 'Cargando...'
              : !query.isError
                ? 'Datos sincronizados'
                : 'Error al sincronizar'}
          </p>
        </div>
      )}

      <div className="flex justify-between items-center">
        <header>
          <h1 className="text-xl font-semibold text-content">Hola, {firstName}</h1>
          <p className="mt-1 text-sm text-muted">
            {includeClosed
              ? 'Todas tus órdenes, incluidas las cerradas.'
              : 'Esto es lo que tienes por hacer.'}
          </p>
        </header>
        <div>
          <Button
            onClick={() => void handleRefresh()}
            // isLoading={query.isFetching}
            disabled={loader}
            aria-label="Refrescar órdenes"
          >
            {query.isFetching ? (
              <Spinner size="sm" />
            ) : (
              <FontAwesomeIcon icon={loader && query.isFetched ? faCheck : faRefresh} />
            )}
          </Button>
        </div>
      </div>

      {/* Dos pestañas y no un botón que cambia de texto: se ve donde se está sin leerlo. */}
      <div className="flex rounded-card border border-line bg-surface p-1">
        {[
          { label: 'Pendientes', value: false },
          { label: 'Historial', value: true },
        ].map((tab) => (
          <button
            key={tab.label}
            type="button"
            aria-pressed={includeClosed === tab.value}
            onClick={() => {
              setIncludeClosed(tab.value);
            }}
            className={`min-h-11 flex-1 rounded-card text-sm font-medium transition-colors ${
              includeClosed === tab.value
                ? 'bg-primary text-white'
                : 'text-muted hover:bg-primary-soft'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {query.isPending ? <LoadingState label="Cargando tus órdenes" /> : null}

      {query.isError ? (
        <ErrorState
          message={
            isApiError(query.error) ? query.error.message : 'No se pudieron cargar tus órdenes.'
          }
          onRetry={() => void query.refetch()}
        />
      ) : null}

      {query.data && orders.length === 0 ? (
        <EmptyState
          title={includeClosed ? 'No tienes ninguna orden' : 'No tienes órdenes pendientes'}
          message={
            includeClosed
              ? 'Cuando el administrador te asigne una ruta apareceran aquí sus paradas y sus materiales.'
              : 'Todo lo que tenías asignado está cerrado. El historial está en la otra pestaña.'
          }
        />
      ) : null}

      <ul className="space-y-3">
        {orders.map((order) => (
          <li key={order.id}>
            <OrderCard order={order} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function OrderCard({ order }: { order: DriverOrder }): ReactNode {
  const schedule = scheduleLabel(order.scheduled_date);
  const isOpen = order.stops_done < order.stops_total;

  return (
    <Link
      to={routeTo(ROUTES.driver.orderDetail, { id: order.id })}
      className="block rounded-card border border-line bg-surface p-4 active:bg-primary-soft"
    >
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                schedule.isToday
                  ? 'bg-accent text-primary'
                  : schedule.isLate
                    ? 'bg-danger-soft text-danger'
                    : 'bg-primary-soft text-muted'
              }`}
            >
              {schedule.isLate ? `Atrasada · ${schedule.text}` : schedule.text}
            </span>
            <Badge tone={ORDER_STATUS_TONES[order.status]}>
              {ORDER_STATUS_LABELS[order.status]}
            </Badge>
          </div>

          <p className="text-sm text-muted">{order.order_number}</p>
        </div>

        <FontAwesomeIcon icon={faChevronRight} aria-hidden="true" className="mt-1 text-muted" />
      </div>

      {order.origin_name && order.destination_name ? (
        <p className="mt-3 flex items-center gap-2 text-sm text-content">
          <FontAwesomeIcon icon={faCircleDot} aria-hidden="true" className="shrink-0 text-muted" />
          <span className="min-w-0 flex-1 truncate">{order.origin_name}</span>
          <FontAwesomeIcon
            icon={faArrowRightLong}
            aria-hidden="true"
            className="shrink-0 text-muted"
          />
          <span className="min-w-0 flex-1 truncate">{order.destination_name}</span>
        </p>
      ) : (
        <p className="mt-3 flex items-center gap-2 text-sm text-muted">
          <FontAwesomeIcon icon={faBoxOpen} aria-hidden="true" />
          Esta orden todavía no tiene recorrido.
        </p>
      )}

      {order.stops_total > 0 ? (
        <div className="mt-3">
          <ProgressBar
            value={order.stops_done}
            total={order.stops_total}
            label={
              isOpen && order.next_stop
                ? `Ahora: ${STOP_TYPE_LABELS[order.next_stop.stop_type].toLowerCase()} en ${
                    order.next_stop.location_name ?? 'la parada'
                  }`
                : 'Recorrido terminado'
            }
          />
        </div>
      ) : null}

      {order.next_stop ? (
        <p className="mt-2 flex items-center gap-2 text-xs text-muted">
          <FontAwesomeIcon icon={faLocationDot} aria-hidden="true" />
          Parada {order.next_stop.sequence_number} de {order.stops_total}
        </p>
      ) : null}
    </Link>
  );
}
